/** Shared visual language for the Money section (Rent Tracker + Accounting). */
export const MONEY = {
  black: '#0a0a0a',
  charcoal: '#171717',
  ink: '#1c1917',
  gold: '#E6A817',
  goldDark: '#b45309',
  green: '#16a34a',
  red: '#dc2626',
  amber: '#d97706',
  cream: '#faf8f5',
  paper: '#f5f5f4',
  border: '#e7e5e4',
  muted: '#78716c',
  mono: "'DM Mono', 'SF Mono', monospace",
  sans: "'DM Sans', system-ui, sans-serif",
};

export const fmtGBP = (n: number) =>
  '£' + n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const fmtGBPShort = (n: number) =>
  '£' + Math.round(n).toLocaleString('en-GB');

export const MONEY_PAGE_CSS = `
  .money-page { padding: 24px; max-width: 1400px; margin: 0 auto; font-family: ${MONEY.sans}; background: ${MONEY.paper}; min-height: 100vh; }
  .money-page * { box-sizing: border-box; }

  .money-banner-wrap {
    margin: -24px -24px 24px;
    padding: 12px 12px 16px;
    background: linear-gradient(155deg, ${MONEY.black} 0%, ${MONEY.charcoal} 42%, #292524 78%, ${MONEY.ink} 100%);
    border-radius: 0 0 22px 22px;
    box-shadow: 0 14px 40px rgba(0,0,0,0.28);
  }
  .money-banner-glass {
    display: flex; align-items: center; justify-content: space-between; gap: 20px; flex-wrap: wrap;
    background: rgba(255,255,255,0.06); backdrop-filter: blur(20px);
    border: 1px solid rgba(255,255,255,0.12); border-radius: 18px;
    padding: 28px 32px; box-shadow: inset 0 1px 0 rgba(255,255,255,0.1);
  }
  .money-banner-glass h1 { margin: 0; font-size: 30px; font-weight: 800; color: #fff; letter-spacing: -0.03em; }
  .money-banner-glass h1 .money-icon { color: ${MONEY.gold}; margin-right: 6px; }
  .money-banner-glass p { margin: 6px 0 0; font-size: 13px; color: rgba(255,255,255,0.72); }
  .money-banner-actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
  .money-btn-ghost {
    background: rgba(255,255,255,0.08); color: #fff; border: 1px solid rgba(255,255,255,0.22);
    border-radius: 10px; padding: 9px 16px; font-weight: 700; font-size: 13px; cursor: pointer;
    font-family: inherit; transition: background .15s;
  }
  .money-btn-ghost:hover { background: rgba(255,255,255,0.14); }
  .money-btn-gold {
    background: ${MONEY.gold}; color: ${MONEY.black}; border: none; border-radius: 10px;
    padding: 10px 20px; font-weight: 800; font-size: 13px; cursor: pointer;
    box-shadow: 0 4px 14px rgba(230,168,23,0.35); font-family: inherit;
  }
  .money-btn-gold:hover { filter: brightness(1.05); }
  .money-btn-dark {
    background: ${MONEY.black}; color: #fff; border: 1.5px solid #404040; border-radius: 10px;
    padding: 9px 16px; font-weight: 700; font-size: 13px; cursor: pointer; font-family: inherit;
  }
  .money-input-dark {
    border: 1.5px solid rgba(255,255,255,0.25); border-radius: 9px; padding: 9px 12px;
    font-size: 13px; font-weight: 600; color: #fff; background: rgba(0,0,0,0.35);
    outline: none; font-family: inherit;
  }
  .money-input-dark option { color: #0f172a; background: #fff; }

  .money-kpi-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 22px; }
  @media (max-width: 900px) { .money-kpi-row { grid-template-columns: repeat(2, 1fr); } }
  .money-kpi {
    border-radius: 14px; padding: 20px 18px; min-height: 100px; position: relative; overflow: hidden;
    transition: transform .12s, box-shadow .12s;
  }
  .money-kpi:hover { transform: translateY(-3px); }
  .money-kpi-black { background: linear-gradient(145deg, ${MONEY.black}, ${MONEY.charcoal}); box-shadow: 0 8px 24px rgba(0,0,0,0.22); border: 1px solid #333; }
  .money-kpi-gold { background: linear-gradient(145deg, ${MONEY.goldDark}, ${MONEY.gold}); box-shadow: 0 8px 24px rgba(230,168,23,0.3); }
  .money-kpi-green { background: linear-gradient(145deg, #15803d, ${MONEY.green}); box-shadow: 0 8px 24px rgba(22,163,74,0.28); }
  .money-kpi-red { background: linear-gradient(145deg, #b91c1c, ${MONEY.red}); box-shadow: 0 8px 24px rgba(220,38,38,0.28); }
  .money-kpi-white { background: #fff; border: 1.5px solid ${MONEY.border}; box-shadow: 0 4px 16px rgba(0,0,0,0.05); }
  .money-kpi .kl { font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 6px; }
  .money-kpi-black .kl, .money-kpi-gold .kl, .money-kpi-green .kl, .money-kpi-red .kl { color: rgba(255,255,255,0.8); }
  .money-kpi-white .kl { color: ${MONEY.muted}; }
  .money-kpi .kv { font-size: 26px; font-weight: 800; line-height: 1.1; font-family: ${MONEY.mono}; }
  .money-kpi-black .kv, .money-kpi-gold .kv, .money-kpi-green .kv, .money-kpi-red .kv { color: #fff; }
  .money-kpi-white .kv { color: ${MONEY.black}; }
  .money-kpi .ks { font-size: 11px; margin-top: 4px; }
  .money-kpi-black .ks, .money-kpi-gold .ks, .money-kpi-green .ks, .money-kpi-red .ks { color: rgba(255,255,255,0.7); }
  .money-kpi-white .ks { color: #a8a29e; }

  .money-toolbar { display: flex; gap: 10px; margin-bottom: 18px; flex-wrap: wrap; align-items: center; }
  .money-search {
    flex: 1; min-width: 200px; max-width: 360px; display: flex; align-items: center; gap: 8px;
    padding: 10px 14px; background: #fff; border: 1.5px solid ${MONEY.border}; border-radius: 10px;
  }
  .money-search input { border: none; outline: none; width: 100%; font-size: 14px; font-family: inherit; }
  .money-filter {
    padding: 10px 36px 10px 14px; border-radius: 10px; border: 1.5px solid ${MONEY.border};
    background: #fff; font-size: 13px; font-weight: 700; color: ${MONEY.black};
    appearance: none; min-width: 180px; cursor: pointer; font-family: inherit;
  }

  .money-alert-slim {
    display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
    padding: 12px 16px; border-radius: 12px; margin-bottom: 16px; font-size: 13px; font-weight: 700;
  }
  .money-alert-slim.red { background: #fef2f2; border: 1.5px solid #fecaca; color: #991b1b; }
  .money-alert-slim.amber { background: #fffbeb; border: 1.5px solid #fde68a; color: #92400e; }

  .money-section-label {
    font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.1em;
    color: ${MONEY.muted}; margin: 0 0 12px; padding-bottom: 8px; border-bottom: 2px solid ${MONEY.black};
    display: flex; align-items: center; justify-content: space-between;
  }

  .money-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 14px; margin-bottom: 20px; }
  .money-card {
    background: #fff; border-radius: 12px; border: 1px solid ${MONEY.border};
    overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.04); display: flex; flex-direction: column;
  }
  .money-card:hover { box-shadow: 0 8px 24px rgba(0,0,0,0.08); }
  .money-card-stripe { height: 4px; }
  .money-card-body { padding: 16px 18px; flex: 1; }
  .money-card-foot { padding: 12px 18px 16px; border-top: 1px solid #f5f5f4; display: flex; gap: 8px; align-items: center; }
  .money-card-addr { font-size: 15px; font-weight: 800; color: ${MONEY.black}; line-height: 1.25; margin-bottom: 4px; }
  .money-card-meta { font-size: 12px; color: ${MONEY.muted}; }
  .money-card-amt { font-family: ${MONEY.mono}; font-size: 22px; font-weight: 600; color: ${MONEY.black}; margin: 10px 0 6px; }
  .money-badge {
    display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; border-radius: 8px;
    font-size: 11px; font-weight: 800; letter-spacing: 0.04em;
  }

  .money-tabs-dark {
    display: flex; gap: 4px; padding: 6px; background: ${MONEY.black}; border-radius: 14px;
    margin-bottom: 20px; overflow-x: auto; scrollbar-width: none;
  }
  .money-tabs-dark::-webkit-scrollbar { display: none; }
  .money-tab {
    padding: 10px 16px; border: none; border-radius: 10px; background: transparent;
    font-size: 13px; font-weight: 700; cursor: pointer; color: rgba(255,255,255,0.55);
    font-family: inherit; white-space: nowrap; transition: all .15s;
  }
  .money-tab:hover { color: rgba(255,255,255,0.85); }
  .money-tab.active { background: ${MONEY.gold}; color: ${MONEY.black}; }

  .money-panel {
    background: #fff; border: 1.5px solid ${MONEY.border}; border-radius: 14px;
    padding: 22px; margin-bottom: 16px;
    background-image: repeating-linear-gradient(transparent, transparent 27px, #f5f5f4 27px, #f5f5f4 28px);
    background-size: 100% 28px; background-position: 0 52px;
  }
  .money-panel-title { font-size: 16px; font-weight: 800; color: ${MONEY.black}; margin: 0 0 16px; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px; }

  .money-table { width: 100%; border-collapse: collapse; font-size: 13px; background: #fff; border-radius: 12px; overflow: hidden; }
  .money-table th {
    padding: 10px 14px; text-align: left; font-size: 10px; font-weight: 800; text-transform: uppercase;
    letter-spacing: 0.07em; color: ${MONEY.muted}; background: ${MONEY.cream}; border-bottom: 2px solid ${MONEY.black};
  }
  .money-table td { padding: 12px 14px; border-bottom: 1px solid #f5f5f4; vertical-align: middle; }
  .money-table tbody tr:hover td { background: #fafaf9; }
  .money-table tbody tr:last-child td { border-bottom: none; }

  .money-empty { text-align: center; padding: 48px 24px; background: #fff; border: 2px dashed ${MONEY.border}; border-radius: 14px; }
  .money-pagination { display: flex; justify-content: center; align-items: center; gap: 12px; margin-top: 20px; font-size: 13px; font-weight: 700; color: ${MONEY.muted}; }
  .money-pagination button {
    padding: 8px 14px; border-radius: 8px; border: 1.5px solid ${MONEY.border}; background: #fff;
    font-weight: 700; cursor: pointer; font-family: inherit;
  }
  .money-pagination button:disabled { opacity: 0.4; cursor: default; }
  .money-pagination button:not(:disabled):hover { border-color: ${MONEY.black}; color: ${MONEY.black}; }

  .money-pay-row { display: flex; gap: 6px; }
  .money-pay-input {
    flex: 1; max-width: 110px; padding: 8px 10px; border: 1.5px solid ${MONEY.black}; border-radius: 8px;
    font-size: 13px; font-family: ${MONEY.mono}; outline: none;
  }
  .money-btn-sm {
    padding: 8px 12px; border-radius: 8px; border: none; font-weight: 800; font-size: 12px;
    cursor: pointer; font-family: inherit;
  }
`;

export const RENT_STATUS = {
  paid:    { label: 'Paid',    bg: '#dcfce7', text: '#15803d', stripe: '#16a34a' },
  partial: { label: 'Partial', bg: '#fef3c7', text: '#b45309', stripe: '#d97706' },
  unpaid:  { label: 'Unpaid',  bg: '#fee2e2', text: '#b91c1c', stripe: '#dc2626' },
  upcoming:{ label: 'Upcoming',bg: '#f5f5f4', text: '#78716c', stripe: '#a8a29e' },
  void:    { label: 'Void',    bg: '#f5f5f4', text: '#a8a29e', stripe: '#d6d3d1' },
} as const;

export const RENT_FILTERS = ['All', 'Unpaid', 'Partial', 'Paid', 'Void'] as const;
